<template>
    <AppModal
        :name="name"
        :beforeClose="beforeClose"
        :beforeOpen="beforeOpen"
        :width="450"
        :clickToClose="false">
        <template slot="header">
            Password change
            <template v-if="!canSkip">
                required
            </template>
        </template>
        <div slot="content" class="text-left">
            <div class="form-group">
                <label>
                    Current password
                </label>
                <AppPasswordInput
                    :value="form.current"
                    @input="onCurrentPasswordInput"
                    placeholder="Current password"
                    id="current-password"/>
                <span v-if="$v.form.current.$error" class="form-text text-danger">
                    <span v-if="!$v.form.current.required">This field is required</span>
                </span>
            </div>

            <div class="form-group">
                <label>
                    New password
                </label>
                <AppPasswordInput
                    :value="form.changeTo"
                    @input="onNewPasswordInput"
                    placeholder="New password"
                    id="new-password"/>
                <div v-if="$v.form.changeTo.$error" class="form-text text-danger">
                    <div v-if="!$v.form.changeTo.required">This field is required</div>
                    <div v-else-if="!$v.form.changeTo.minLength">
                        Password length should be more than {{ $v.form.changeTo.$params.minLength.min }} characters
                    </div>
                    <div v-else-if="!$v.form.changeTo.hasSpecialCharacter">
                        Password should have at least one special character
                    </div>
                    <div v-else-if="!$v.form.changeTo.hasUniqueCharacters">
                        Password should have at least 4 unique characters
                    </div>
                </div>
            </div>

            <div class="form-group">
                <label>
                    Confirm new password
                </label>
                <AppPasswordInput
                    :value="form.changeToConfirm"
                    @input="onConfirmPasswordInput"
                    placeholder="Confirm new password"
                    id="confirm-password"/>
                <div v-if="$v.form.changeToConfirm.$error" class="form-text text-danger">
                    <div v-if="!$v.form.changeToConfirm.sameAsChangeTo">
                        Passwords do not match
                    </div>
                </div>
            </div>
        </div>

        <template slot="actions">
            <div class="text-right">
                <button v-if="canSkip" @click="closeModal" class="btn btn-link mt-3">Close</button>
                <button v-loading.btn="isLoading" @click="changePassword" class="btn btn-primary">
                    Update
                </button>
            </div>
        </template>
    </AppModal>
</template>

<script>
import {minLength, required, sameAs} from 'vuelidate/lib/validators'
import profileObj from '../../../command/profile'
import {ERROR, SUCCESS} from '../../../store/modules/ui/UI'
import {hasSpecialCharacter, hasUniqueCharacters} from '../../../utils/validators'
import AppPasswordInput from '../../../components/shared/AppPasswordInput'

export default {
    name: 'ChangePassword',
    components: {AppPasswordInput},
    props: {
        name: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            form: {
                current: '',
                changeTo: '',
                changeToConfirm: ''
            },
            canSkip: true,
            isLoading: false,
            touchMap: new WeakMap()
        }
    },
    validations() {
        return {
            form: {
                current: {
                    required
                },
                changeTo: {
                    required,
                    minLength: minLength(4),
                    hasSpecialCharacter,
                    hasUniqueCharacters: v => hasUniqueCharacters(v, 4)
                },
                changeToConfirm: {
                    sameAsChangeTo: sameAs('changeTo')
                }
            }
        }
    },
    methods: {
        beforeOpen({params}) {
            if (_.has(params, 'canSkip')) {
                this.canSkip = params.canSkip
            }
        },

        beforeClose() {
            Object.assign(this.$data, this.$options.data.apply(this))
            this.$v.$reset()
        },

        closeModal() {
            this.$modal.hide(this.name)
        },

        onCurrentPasswordInput(password) {
            this.$set(this.form, 'current', password)
            this.vuelidateDelayTouch(this.$v.form.current, this.touchMap)
        },

        onNewPasswordInput(password) {
            this.$set(this.form, 'changeTo', password)
            this.vuelidateDelayTouch(this.$v.form.changeTo, this.touchMap)
        },

        onConfirmPasswordInput(password) {
            this.$set(this.form, 'changeToConfirm', password)
            this.vuelidateDelayTouch(this.$v.form.changeToConfirm, this.touchMap)
        },

        changePassword() {
            if (this.$v.$invalid) {
                this.$v.$touch()
                return
            }
            this.isLoading = true
            profileObj.changePassword({
                current: this.form.current,
                changeTo: this.form.changeTo
            })
                .then(() => {
                    this.$snackbar.show({type: SUCCESS, text: 'Password changed successfully'})
                    this.closeModal()
                })
                .catch(err => {
                    this.$snackbar.show({
                        type: ERROR,
                        text: err.response.status === 400
                            ? 'Your current password doesn\'t match.'
                            : 'We were unable to update your password please try again later.'
                    })
                })
                .finally(() => {
                    this.isLoading = false
                })
        }
    }
}
</script>

<style scoped>
    button {
        width: 120px;
    }
</style>
